import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Grid,
  FormHelperText,
  IconButton,
  CircularProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
} from '@material-ui/icons';
import InfoIcon from '@material-ui/icons/Info';
import { Autocomplete } from '@material-ui/lab';
import { Select } from 'components';
import { PMS_SERVICES } from 'Services';
import { FIRM_NAME_MAPPING, isStandaloneLocation } from 'Views/Common/enum';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';

const INSTANCE_TYPES = [
  { value: 'standalone', label: 'Standalone' },
  { value: 'clinic', label: 'Clinic' },
];


const PmsDropdown = (props) => {
  const { dispatch: globalDispatch } = useContext(Context);

  const dispatchError = (message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity: 'error', message } },
    });

  const {
    isLoading,
    data: vendorsList = [],
    vendorName,
    onVendorSelect,
    vendorFirmname,
    onFirmnameChange,
    handleValidateFirmname,
    validateLoading,
    captureLocationId = false,
    pmsLocationId,
    pmsLocationDisplayId,
    onLocIdChange,
    handleValidateLocationId,
    validateLocationLoading,
    reqFields,
    disabled = false,
    children,
    onInstanceTypeChange,
    instanceType,
    onFirmSelect,
    onLocationValidated,
  } = props;

  const [firmsList, setFirmsList] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(false);
  const [firmsFetched, setFirmsFetched] = useState(false);
  const [firmValidating, setFirmValidating] = useState(false);
  const [locationsList, setLocationsList] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsFetched, setLocationsFetched] = useState(false);
  const [locationValidating, setLocationValidating] = useState(false);
  const [vendorMetadata, setVendorMetadata] = useState({});
  const [manualLocationEntry, setManualLocationEntry] = useState(false);

  useEffect(() => {
    fetchVendorMetadata();
  }, []);

  useEffect(() => {
    if (vendorName) {
      fetchFirmsForVendor(vendorName);
      setManualLocationEntry(false);
    } else {
      setFirmsList([]);
      setFirmsFetched(false);
    }
  }, [vendorName]);

  useEffect(() => {
    if (vendorFirmname && instanceType === 'clinic') {
      fetchLocationsForFirm(vendorName, vendorFirmname);
      setManualLocationEntry(false);
    } else {
      setLocationsList([]);
      setLocationsFetched(false);
    }
  }, [vendorFirmname, instanceType]);

  const vendorInstanceType = vendorMetadata[vendorName] || 'both';

  const fetchVendorMetadata = async () => {
    try {
      const response = await PMS_SERVICES.getVendorSettings();
      if (response?.type === 'success' && response?.data) {
        const metaMap = {};
        response.data.forEach((v) => {
          metaMap[v.vendor] = v.instance_type || 'both';
        });
        setVendorMetadata(metaMap);
      }
    } catch (e) {
      console.error('Failed to load vendor configuration:', e);
      dispatchError('Failed to load vendor configuration');
    }
  };

  const fetchFirmsForVendor = async (vendor) => {
    setFirmsLoading(true);
    setFirmsFetched(false);
    try {
      const response = await PMS_SERVICES.getFirmsByVendor(vendor);
      if (response?.type === 'success') {
        setFirmsList(response?.data?.firms || []);
      } else {
        setFirmsList([]);
      }
    } catch (e) {
      console.error('Failed to fetch firms:', e);
      setFirmsList([]);
    }
    setFirmsLoading(false);
    setFirmsFetched(true);
  };

  const fetchLocationsForFirm = async (vendor, firmName) => {
    setLocationsLoading(true);
    setLocationsFetched(false);
    try {
      const response = await PMS_SERVICES.getLocationsByVendorFirm(vendor, firmName);
      if (response?.type === 'success') {
        setLocationsList(response?.data?.locations || []);
      } else {
        setLocationsList([]);
      }
    } catch (e) {
      console.error('Failed to fetch locations:', e);
      setLocationsList([]);
    }
    setLocationsLoading(false);
    setLocationsFetched(true);
  };

  const validateLocationAndNotify = async (locationId) => {
    if (!vendorName || !vendorFirmname || !locationId || !onLocationValidated) return;
    setLocationValidating(true);
    try {
      const payload = {
        vendor: vendorName,
        pms_firm_name: vendorFirmname,
        pms_location_id: locationId,
      };
      const vResponse = await PMS_SERVICES.validatePms(payload);
      if (vResponse?.type === 'success') {
        const respData = vResponse?.data || {};
        onLocationValidated({
          locationId,
          displayId: respData.pms_location_display_id || null,
          // Relay clinics also return the PMS system, identity scope and
          // owning DSO id; forward them so consumers can persist pms_info.
          pmsSystem: respData.pms_system || null,
          pmsScope: respData.pms_scope || null,
          dsoId: respData.dso_id || null,
          isValid: true,
        });
      } else {
        dispatchError(vResponse?.error || vResponse?.message || 'Invalid location');
        onLocIdChange({ target: { value: '' } });
        onLocationValidated({ locationId: '', displayId: null, isValid: false });
      }
    } catch (e) {
      console.error('Location validation failed:', e);
      dispatchError('Invalid location');
      onLocIdChange({ target: { value: '' } });
      onLocationValidated({ locationId: '', displayId: null, isValid: false });
    }
    setLocationValidating(false);
  };

  const clearFirmSelection = () => {
    if (onFirmSelect) {
      onFirmSelect({ target: { value: '' } });
    } else {
      onFirmnameChange({ target: { value: '' } });
    }
  };

  const validateFirmAndAutoSelectInstance = async (vendor, firmName) => {
    if (!vendor || !firmName || !onInstanceTypeChange) return;

    const metaInstanceType = vendorMetadata[vendor] || 'both';

    if (metaInstanceType !== 'both') {
      onInstanceTypeChange(metaInstanceType);
      return;
    }

    setFirmValidating(true);
    try {
      const key = FIRM_NAME_MAPPING[vendor] || 'pms_firm_name';
      const payload = { vendor, [key]: firmName };
      const vResponse = await PMS_SERVICES.validatePms(payload);
      if (vResponse?.type === 'success') {
        const locationRequired = vResponse?.data?.location_required;
        onInstanceTypeChange(locationRequired === false ? 'standalone' : 'clinic');
      } else {
        dispatchError(vResponse?.error || vResponse?.message || 'Invalid firm');
        clearFirmSelection();
      }
    } catch (e) {
      console.error('Firm validation failed:', e);
      dispatchError('Invalid firm');
      clearFirmSelection();
    }
    setFirmValidating(false);
  };

  const renderEndAdornment = () => {
    if (validateLoading) {
      return (
        <IconButton size="small" data-testid="firm-loading-button">
          <CircularProgress size={20} color="primary" />
        </IconButton>
      );
    } else if (reqFields?.vendor_firmname?.error) {
      return (
        <Tooltip title="Not a valid firm">
          <IconButton size="small" data-testid="firm-error-button">
            <ErrorIcon className="color_error" />
          </IconButton>
        </Tooltip>
      );
    } else if (reqFields?.vendor_firmname?.isValid) {
      return (
        <Tooltip title="Successfully validated">
          <IconButton size="small" data-testid="firm-success-button">
            <CheckCircleIcon className="color_success" />
          </IconButton>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Click to validate">
          <IconButton
            size="small"
            onClick={handleValidateFirmname}
            data-testid="firm-validate-button"
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      );
    }
  };

  const renderLocationEndAdornment = () => {
    if (validateLocationLoading) {
      return (
        <IconButton size="small" data-testid="location-loading-button">
          <CircularProgress size={20} color="primary" />
        </IconButton>
      );
    } else if (reqFields?.pms_location_id?.error) {
      return (
        <Tooltip title="Not a valid location id">
          <IconButton size="small" data-testid="location-error-button">
            <ErrorIcon className="color_error" />
          </IconButton>
        </Tooltip>
      );
    } else if (reqFields?.pms_location_id?.isValid) {
      return (
        <Tooltip title="Successfully validated">
          <IconButton size="small" data-testid="location-success-button">
            <CheckCircleIcon className="color_success" />
          </IconButton>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="Click to validate">
          <IconButton
            size="small"
            onClick={handleValidateLocationId}
            data-testid="location-validate-button"
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      );
    }
  };

  const firmIsLoading = firmsLoading || firmValidating;
  const showFirmInput = firmsFetched && firmsList.length === 0;
  const locationIsLoading = locationsLoading || locationValidating;
  const hasLocationDropdown =
    instanceType === 'clinic' && locationsFetched && locationsList.length > 0;

  const renderFirmInfoIcon = () => (
    <Tooltip title="If firm is not found, create one using the PMS Firm Configuration section above">
      <InfoIcon
        color="primary"
        fontSize="small"
        style={{ marginLeft: 4, cursor: 'pointer' }}
      />
    </Tooltip>
  );

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {/* Vendor Select */}
      <Grid item xs={12} lg={3}>
        <FormControl
          fullWidth
          required
          variant="outlined"
          disabled={isLoading || disabled}
        >
          <InputLabel htmlFor="outlined-Group-native-simple">
            Vendors
          </InputLabel>
          <Select
            id="pms-select-vendor"
            label="Vendors"
            name="vendor_id"
            value={vendorName}
            onChange={onVendorSelect}
          >
            <MenuItem value="">Select Vendor</MenuItem>
            {[...(vendorsList || [])]
              .sort((a, b) =>
                String(a).localeCompare(String(b), undefined, {
                  sensitivity: 'base',
                }),
              )
              .map((vendor, i) => (
                <MenuItem
                  value={vendor}
                  key={`${i}_${vendor}`}
                  data-testid={`pms-state-select-item-${i}`}
                >
                  {vendor}
                </MenuItem>
              ))}
          </Select>
          {reqFields?.vendor_name?.text && reqFields?.vendor_name?.error && (
            <FormHelperText className="color_error">
              {reqFields.vendor_name.text}
            </FormHelperText>
          )}
        </FormControl>
      </Grid>

      {/* Firm Name - Dropdown if backend returned firms, TextField otherwise */}
      {vendorName && (
        <Grid item xs={12} lg={3} className="position-relative">
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              {showFirmInput ? (
                <TextField
                  fullWidth
                  required
                  name="firm_name"
                  label="Firm Name"
                  value={vendorFirmname}
                  variant="outlined"
                  placeholder="Firm name"
                  onChange={onFirmnameChange}
                  error={reqFields?.vendor_firmname?.error}
                  helperText={reqFields?.vendor_firmname?.text}
                  disabled={disabled || validateLoading}
                  InputProps={{
                    endAdornment:
                      vendorFirmname && !disabled && renderEndAdornment(),
                  }}
                  inputProps={{
                    'data-testid': 'firm-name-input',
                  }}
                />
              ) : (
                <FormControl
                  fullWidth
                  required
                  variant="outlined"
                  disabled={disabled || firmIsLoading}
                >
                  <InputLabel>Firm Name</InputLabel>
                  <Select
                    label="Firm Name"
                    value={vendorFirmname || ''}
                    onChange={(e) => {
                      const selectedFirm = e.target.value;
                      if (onFirmSelect) {
                        onFirmSelect(e);
                      } else {
                        onFirmnameChange(e);
                      }
                      if (selectedFirm) {
                        validateFirmAndAutoSelectInstance(vendorName, selectedFirm);
                      }
                    }}
                  >
                    <MenuItem value="">Select Firm</MenuItem>
                    {firmsList.map((firm, i) => (
                      <MenuItem value={firm} key={`firm_${i}_${firm}`}>
                        {firm}
                      </MenuItem>
                    ))}
                  </Select>
                  {reqFields?.vendor_firmname?.error && (
                    <FormHelperText className="color_error">
                      {reqFields.vendor_firmname.text}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
              {firmIsLoading && (
                <CircularProgress size={20} thickness={5} style={{ position: 'absolute', top: '40%', right: 20 }} />
              )}
            </div>
            {firmsFetched && !firmIsLoading && renderFirmInfoIcon()}
          </div>
        </Grid>
      )}

      {/* Instance Type — only shown for vendors that support both modes */}
      {captureLocationId && vendorName && vendorFirmname && !firmValidating && !(disabled && pmsLocationId) && vendorInstanceType === 'both' && (
        <Grid item xs={12} lg={3}>
          <FormControl fullWidth variant="outlined" disabled={disabled}>
            <InputLabel>Select Instance</InputLabel>
            <Select
              label="Select Instance"
              value={instanceType || ''}
              onChange={(e) =>
                onInstanceTypeChange && onInstanceTypeChange(e.target.value)
              }
            >
              <MenuItem value="">Select Instance</MenuItem>
              {INSTANCE_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Location - Dropdown if backend returned locations, TextField otherwise */}
      {/* Show when: setting up a clinic instance, OR when already saved with a
          real mapped location. The standalone sentinel (no location) must not
          surface the field for standalone vendors (e.g. Twelve). */}
      {captureLocationId && vendorName && (instanceType === 'clinic' || (disabled && pmsLocationId && !isStandaloneLocation(pmsLocationId))) && !firmValidating && (
        <Grid item xs={12} lg={3} className="position-relative">
          {(hasLocationDropdown && !manualLocationEntry) || locationIsLoading ? (
            <>
              <Autocomplete
                options={locationsList}
                getOptionLabel={(opt) =>
                  typeof opt === 'string' ? opt : `${opt.name} (${opt.pms_location_id})`
                }
                getOptionSelected={(opt, val) =>
                  opt.pms_location_id === (typeof val === 'string' ? val : val?.pms_location_id)
                }
                value={
                  locationsList.find((l) => l.pms_location_id === pmsLocationId) || null
                }
                onChange={(_, selected) => {
                  const selectedLocationId = selected?.pms_location_id || '';
                  onLocIdChange({ target: { value: selectedLocationId } });
                  if (selectedLocationId) {
                    validateLocationAndNotify(selectedLocationId);
                  }
                }}
                disabled={disabled || locationIsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                    variant="outlined"
                    required
                    error={reqFields?.pms_location_id?.error}
                    helperText={reqFields?.pms_location_id?.text}
                    inputProps={{
                      ...params.inputProps,
                      'data-testid': 'location-autocomplete-input',
                    }}
                  />
                )}
              />
              {!disabled && !locationIsLoading && (
                <Typography
                  variant="caption"
                  color="primary"
                  style={{ cursor: 'pointer', marginTop: 4, display: 'inline-block' }}
                  onClick={() => {
                    setManualLocationEntry(true);
                    onLocIdChange({ target: { value: '' } });
                  }}
                >
                  Can&apos;t find your location?
                </Typography>
              )}
            </>
          ) : (
            <>
              <TextField
                fullWidth
                required
                name="pms_location_id"
                label="Location Id"
                value={
                  pmsLocationDisplayId && !manualLocationEntry
                    ? `${pmsLocationId} (${pmsLocationDisplayId})`
                    : pmsLocationId
                }
                variant="outlined"
                placeholder="Location Id"
                onChange={onLocIdChange}
                error={reqFields?.pms_location_id?.error}
                helperText={reqFields?.pms_location_id?.text}
                disabled={disabled || locationValidating}
                InputProps={{
                  endAdornment:
                    pmsLocationId &&
                    !disabled &&
                    renderLocationEndAdornment(),
                }}
                inputProps={{
                  'data-testid': 'nexhealth-location-id',
                }}
                InputLabelProps={{
                  shrink: !!pmsLocationId,
                }}
              />
              {manualLocationEntry && hasLocationDropdown && !disabled && (
                <Typography
                  variant="caption"
                  color="primary"
                  style={{ cursor: 'pointer', marginTop: 4, display: 'inline-block' }}
                  onClick={() => {
                    setManualLocationEntry(false);
                    onLocIdChange({ target: { value: '' } });
                  }}
                >
                  Select from list
                </Typography>
              )}
            </>
          )}
          {locationIsLoading && (
            <CircularProgress size={20} thickness={5} style={{ position: 'absolute', top: '40%', right: 20 }} />
          )}
        </Grid>
      )}

      {children}
    </Grid>
  );
};

PmsDropdown.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.string),
  vendorName: PropTypes.string,
  onVendorSelect: PropTypes.func,
  vendorFirmname: PropTypes.string,
  onFirmnameChange: PropTypes.func,
  handleValidateFirmname: PropTypes.func,
  validateLoading: PropTypes.bool,
  captureLocationId: PropTypes.bool,
  pmsLocationId: PropTypes.string,
  pmsLocationDisplayId: PropTypes.string,
  onLocIdChange: PropTypes.func,
  handleValidateLocationId: PropTypes.func,
  validateLocationLoading: PropTypes.bool,
  reqFields: PropTypes.shape({
    vendor_name: PropTypes.shape({
      text: PropTypes.string,
      error: PropTypes.bool,
    }),
    vendor_firmname: PropTypes.shape({
      text: PropTypes.string,
      error: PropTypes.bool,
      isValid: PropTypes.bool,
    }),
    pms_location_id: PropTypes.shape({
      text: PropTypes.string,
      error: PropTypes.bool,
      isValid: PropTypes.bool,
    }),
  }),
  disabled: PropTypes.bool,
  children: PropTypes.node,
  onInstanceTypeChange: PropTypes.func,
  instanceType: PropTypes.string,
  onFirmSelect: PropTypes.func,
  onLocationValidated: PropTypes.func,
};

export default PmsDropdown;
